import CoursePathFilters from '@/components/admin/courses/CoursePathFilters';
import CoursePathItem from '@/components/admin/courses/CoursePathItem';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import InfinityList from '@/components/ui/custom/InfinityList';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useDebounce from '@/data/hook/useDebounce';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useToggle from '@/data/hook/useToggle';
import { ListCoursePathFilters } from '@/data/services/coursePathServices';
import { deleteCourse } from '@/data/services/courseServices';
import {
  courseBasesQueryKeys,
  coursePathsQueryKeys,
} from '@/data/services/querykeys';
import { CourseFormatEnum } from '@/models/Course';
import CoursePath from '@/models/CoursePath';
import alert from '@/utils/shared/alert';
import useFilterParams from '@/utils/UseFilterParams';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { searchParamsToObject } from '@/utils/searchParamsToObject';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Text from '../../Text';
import CoursePathGroupBy, { CoursePathGroupByEnum } from './CoursePathGroupBy';

type TabType = CourseFormatEnum | 'ALL';

export interface Tab {
  id: TabType;
  name: string;
}

export default function CoursesPathList() {
  const { filterParams, setFilterParams } =
    useFilterParams<ListCoursePathFilters>();
  const [debouncedSearch, setDebouncedSearch] = useState<string>(
    filterParams.get('search') || '',
  );
  const filters = searchParamsToObject<ListCoursePathFilters>(filterParams, {
    courseId: {
      type: 'number',
      isArray: true,
    },
    formats: {
      type: 'string',
      isArray: true,
    },
    businessModels: {
      type: 'string',
      isArray: true,
    },
    courseLineId: {
      type: 'number',
      isArray: true,
    },
    versionMax: { type: 'number', isArray: true },
    versionMin: { type: 'number', isArray: true },
    status: { type: 'string', isArray: true },
  });
  const [groupBy, setGroupBy] = useState(CoursePathGroupByEnum.LINE);
  const { debounce, cancel: cancelSearch } = useDebounce(800);

  function getUpdatedFilters(newValues: ListCoursePathFilters) {
    return { ...filters, ...newValues };
  }

  function handleDebouncedSearch(search: string = '') {
    if (!search) {
      setDebouncedSearch(search);
      return cancelSearch();
    }
    debounce(() => setDebouncedSearch(search));
  }

  function updateFilters(newValues: ListCoursePathFilters) {
    if ('search' in newValues) {
      return handleDebouncedSearch(newValues.search);
    }

    const updatedFilters = getUpdatedFilters(newValues);

    setFilterParams(updatedFilters);
  }

  useEffect(() => {
    setFilterParams({ search: debouncedSearch });
  }, [debouncedSearch, setFilterParams]);

  const { isOpen, toggle } = useToggle();

  const seletedCourseRef = useRef<CoursePath | null>(null);

  const [courseName, setCourseName] = useState<string>('');

  const queryClient = useQueryClient();

  const { mutate, isPending: isDiscarding } = useMutation(deleteCourse, {
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: coursePathsQueryKeys.list._def,
      });
      queryClient.invalidateQueries({
        queryKey: courseBasesQueryKeys.list._def,
      });
    },
    onError(error) {
      alert.error(getErrorMessage(error));
    },
    onSettled() {
      toggle();
    },
  });

  function handleSelectCourse(coursePath: CoursePath) {
    seletedCourseRef.current = coursePath;

    const name = `${coursePath.course.name} v${coursePath.version.toFixed(1)}`;
    setCourseName(name);

    toggle();
  }

  return (
    <div className="flex flex-col gap-2.5">
      <ModalDisable
        onClickCancel={toggle}
        onClickConfirm={() =>
          seletedCourseRef.current && mutate(seletedCourseRef.current.course.id)
        }
        translationString="modalDeleteCourse"
        visible={isOpen}
        modalType="delete"
        isRequesting={isDiscarding}
        selectedObjectName={courseName}
      />
      <CoursePathFilters
        filters={filters}
        groupBy={groupBy}
        updateFilters={updateFilters}
        updateGroupBy={setGroupBy}
      />
      <List
        filters={filters}
        groupBy={groupBy}
        handleSelectCourse={handleSelectCourse}
      />
    </div>
  );
}

type CoursesPathsListProps = {
  filters: ListCoursePathFilters;
  groupBy: CoursePathGroupByEnum;
  handleSelectCourse: (course: CoursePath) => void;
};

function List({ filters, groupBy, handleSelectCourse }: CoursesPathsListProps) {
  const [tCommon] = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const defaultFilter: ListCoursePathFilters = {
    ...filters,
    uniqueCourse: true,
  };
  const {
    results: courses,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
  } = useInfiniteService({
    ...coursePathsQueryKeys.list(defaultFilter)._ctx.infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    enabled: groupBy === CoursePathGroupByEnum.NONE,
  });

  const loadingCards = Array.from({ length: 20 }).map((_, index) => (
    <CoursePathItem isLoading key={index} />
  ));

  if (groupBy !== CoursePathGroupByEnum.NONE) {
    return (
      <CoursePathGroupBy
        key={groupBy}
        filters={defaultFilter}
        groupBy={groupBy}
        handleSelectCourse={handleSelectCourse}
      />
    );
  }

  return (
    <InfinityList
      hasNextPage={hasNextPage}
      onReachEnd={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      className="grid grid-cols-2 md:grid-cols-4 gap-2.5"
    >
      <ConditionalRenderer condition={!isLoading} fallback={loadingCards}>
        {courses.map(coursePath => (
          <CoursePathItem
            coursePath={coursePath}
            key={coursePath.id}
            handleSelectCourse={handleSelectCourse}
          />
        ))}
        <ConditionalRenderer condition={courses.length === 0}>
          <div className="col-span-full h-full min-h-40 flex items-center justify-center">
            <Text text={tCommon('noResults')} className="text-center" />
          </div>
        </ConditionalRenderer>
      </ConditionalRenderer>
    </InfinityList>
  );
}
